import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Edit from "../../../assets/svgs/Pencil.svg";
import useAuth from "../../../hooks/useAuth";
import BaseLayout from "../../../layouts/BaseLayout";
import { BASE_URL } from "../../../utils/Element";
import AddCommission from "./AddCommission";
import AddKeyContract from "./AddKeyContract";
import CompleteStatusModal from "./CompleteStatusModal";

const StageDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [config] = useAuth();

  const [loading, setLoading] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [keyContacts, setKeyContacts] = useState({});
  const [commissionBreakdown, setCommissionBreakdown] = useState({});
  const [contractData, setContractData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMilestoneIndex, setSelectedMilestoneIndex] = useState(null);
  const [completeModal, setCompleteModal] = useState(false);
  const [comment, setComment] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedPrice, setEditedPrice] = useState("");
  const [isEditingCapRate, setIsEditingCapRate] = useState(false);
  const [editedCapRate, setEditedCapRate] = useState("");

  // Get current tab from URL (default to 0 = Transaction Details)
  const currentTab = parseInt(searchParams.get("tab")) || 0;

  const milestoneRows = [
    ["Purchase Agreement Draft Distributed", "purchase_agreement_draft_distributed"],
    ["Executed Purchase Agreement", "executed_purchase_agreeement"],
    ["Earnest Deposit Confirmed", "earnest_deposite_confirmed"],
    ["Due Diligence Delivery Confirmed", "due_diligence_delivery_receipt_confirmed"],
    ["Title", "title"],
    ["Coordinate Inspections", "cordinate_inspection"],
    ["Due Diligence Expiration Date", "due_diligence_expiration_date"],
    ["Estoppel & SNDA Ordered", "estoppel_snda_ordered"],
    ["Financing Approval", "financing_approval"],
    ["Estoppel & SNDA Received", "estoppel_snda_received"],
    ["Commission Invoice & Wiring Instructions", "comission_invoice_wiring_instruction"],
    ["Scheduled Closing", "scheduled_closing"]
  ];

  const getMilestoneLabel = (key) => {
    const labels = {
      executed_loi: "Executed LOI",
      purchase_agreement_draft_distributed: "PA Draft Distributed",
      executed_purchase_agreeement: "Executed PSA",
      earnest_deposite_confirmed: "Earnest Deposit",
      due_diligence_delivery_receipt_confirmed: "DD Delivery",
      title: "Title",
      cordinate_inspection: "Inspections",
      due_diligence_expiration_date: "DD Expiration",
      estoppel_snda_ordered: "Estoppel/SNDA Ordered",
      financing_approval: "Financing Approval",
      estoppel_snda_received: "Estoppel/SNDA Received",
      comission_invoice_wiring_instruction: "Commission Invoice",
      scheduled_closing: "Scheduled Closing",
      closed_date: "Closed",
    };
    return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleStatusChange = (index, newStatus) => {
    if (newStatus === "Completed") {
      setSelectedMilestoneIndex(index);
      setCompleteModal(true);
      return;
    }

    const updated = [...milestones];
    if (!updated[index]) updated[index] = { status: "Pending", completedAt: null };

    updated[index].status = newStatus;
    updated[index].completedAt = null;
    setMilestones(updated);

    const backendKey = milestones[index]?.key;

    axios.post(`${BASE_URL}/contract-to-close-milestone/${id}`, {
      key_name: backendKey,
      status: newStatus,
    }, config);
  };

  const handleCommentSubmit = async () => {
    if (!comment.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/contract-to-close-comment/${id}`, { comment }, config);
      setComment("");
      setShowCommentBox(false);
      fetchContractDetails();
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompletedDate = async (date) => {
    if (selectedMilestoneIndex === null) return;

    const backendKey = milestones[selectedMilestoneIndex]?.key;

    await axios.post(`${BASE_URL}/contract-to-close-milestone/${id}`, {
      key_name: backendKey,
      status: "Completed",
      completed_date: date,
    }, config);

    setCompleteModal(false);
    fetchContractDetails();
    setSelectedMilestoneIndex(null);
  };

  const handleSavePrice = async () => {
    setIsEditingPrice(false);
    const rawPrice = parseFloat(editedPrice.replace(/,/g, "").replace("$", ""));
    if (isNaN(rawPrice)) return;

    const currentPrice = parseFloat(contractData?.executed?.contract_price_format?.replace(/,/g, "") || 0);
    const currentCapRate = parseFloat(contractData?.executed?.contract_cap_rate_format || 0);
    const noi = currentPrice * (currentCapRate / 100);

    let payload = { contract_price: rawPrice };
    if (noi > 0 && rawPrice > 0) {
      payload.contract_cap_rate = (noi / rawPrice) * 100;
    }

    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/contract-to-close-comment/${id}`, payload, config);
      fetchContractDetails();
    } catch (error) {
      console.error("Error updating price:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCapRate = async () => {
    setIsEditingCapRate(false);
    const rawCapRate = parseFloat(editedCapRate.replace(/%/g, "").replace(/,/g, ""));
    if (isNaN(rawCapRate)) return;

    const currentPrice = parseFloat(contractData?.executed?.contract_price_format?.replace(/,/g, "") || 0);
    const currentCapRate = parseFloat(contractData?.executed?.contract_cap_rate_format || 0);
    const noi = currentPrice * (currentCapRate / 100);

    let payload = { contract_cap_rate: rawCapRate };
    if (noi > 0 && rawCapRate > 0) {
      payload.contract_price = noi / (rawCapRate / 100);
    }

    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/contract-to-close-comment/${id}`, payload, config);
      fetchContractDetails();
    } catch (error) {
      console.error("Error updating cap rate:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContractDetails = () => {
    setLoading(true);
    axios.get(`${BASE_URL}/contract-to-close/${id}`, config)
      .then((res) => {
        const data = res?.data?.data;
        if (!data) return;

        setContractData(data);

        const milestoneKeys = [
          "purchase_agreement_draft_distributed", "executed_purchase_agreeement",
          "earnest_deposite_confirmed", "due_diligence_delivery_receipt_confirmed",
          "title", "cordinate_inspection", "due_diligence_expiration_date",
          "estoppel_snda_ordered", "financing_approval", "estoppel_snda_received",
          "comission_invoice_wiring_instruction", "scheduled_closing"
        ];

        const filledMilestones = milestoneKeys.map((key) => {
          const statusKey = `${key}_status`;
          const completedDateKey = `${key}_completed_date`;
          const rawStatus = data[statusKey];

          return {
            key,
            label: getMilestoneLabel(key),
            status: (rawStatus === 3 || rawStatus === "3") ? "Completed" :
              (rawStatus === 2 || rawStatus === "2") ? "In Progress" : "Pending",
            completedAt: data[completedDateKey] ? new Date(data[completedDateKey]) : null,
            rawStatus,
            rawCompletedDate: data[completedDateKey],
          };
        });

        setMilestones(filledMilestones);
        setKeyContacts(data?.key_contact || {});
        setCommissionBreakdown(data?.gross_commission || {});
      })
      .catch((err) => console.error("Failed to fetch contract details:", err))
      .finally(() => setLoading(false));
  };

  const calculateBrokerShare = () => {
    const brokers = Array.isArray(contractData?.broker_breakdowns) ? contractData.broker_breakdowns : [];
    return brokers.reduce((sum, item) => sum + Number(item.total_value || 0), 0);
  };

  const calculateBdsShare = () => {
    const bds = Array.isArray(contractData?.bds_breakdowns) ? contractData.bds_breakdowns : [];
    return bds.reduce((sum, item) => sum + Number(item?.total_value ?? 0), 0);
  };

  const calculateCompanyGross = () => {
    const gci = contractData?.gross_commission_income ||
      parseFloat(contractData?.executed?.gross_commission?.gci_to_company?.replace(/,/g, '') || 0);
    return gci - (calculateBrokerShare() + calculateBdsShare());
  };

  useEffect(() => {
    fetchContractDetails();
  }, [id]);

  // Back Navigation
  const handleBack = () => {
    navigate(`/contact-close?tab=${currentTab}`);
  };

  return (
    <BaseLayout>
      <div className="mb-[20px]">
        <p
          role="button"
          className="tgreen-H head-5 underline cursor-pointer flex items-center gap-1 hover:text-green-700"
          onClick={handleBack}
        >
          ← Back to Contract to Close
        </p>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-md">
        <div className="mb-6">
          <h2 className="head-2 dark-H">
            {contractData?.link?.property_name || ""} -{" "}
            <span className="text-gray-600">
              {[
                contractData?.link?.street_address,
                contractData?.link?.city,
                contractData?.link?.state,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-6 gap-4 mb-8">
          {[
            ["CONTRACT PRICE", `$${contractData?.executed?.contract_price_format || 0}`],
            ["CONTRACT CAP RATE ", `${contractData?.executed?.contract_cap_rate_format || 0}%`],
            ["SCHEDULED CLOSING DATE", contractData?.scheduled_closing],
            ["COMMISSION %", contractData?.executed?.company_gcl || 0],
            ["GCI TO COMPANY", `$${contractData?.executed?.gross_commission?.gci_to_company || 0}`],
          ].map(([label, value], idx) => (
            <div
              key={idx}
              className={`p-4 rounded-2xl shadow-sm text-center bg-gray-100 ${label === "CONTRACT PRICE" || label === "CONTRACT CAP RATE " ? "cursor-pointer" : ""}`}
              onClick={() => {
                if (label === "CONTRACT PRICE" && !isEditingPrice) {
                  setIsEditingPrice(true);
                  setEditedPrice(contractData?.executed?.contract_price_format || "0");
                } else if (label === "CONTRACT CAP RATE " && !isEditingCapRate) {
                  setIsEditingCapRate(true);
                  setEditedCapRate(`${contractData?.executed?.contract_cap_rate_format || 0}%`);
                }
              }}
            >
              <p className="uppercase body-S dark-M">{label}</p>
              {label === "CONTRACT PRICE" && isEditingPrice ? (
                <input
                  className="head-2 dark-H mt-1 text-center w-full border border-gray-300 rounded-md"
                  value={editedPrice}
                  onChange={(e) => setEditedPrice(e.target.value)}
                  onBlur={handleSavePrice}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSavePrice();
                    }
                  }}
                  autoFocus
                />
              ) : label === "CONTRACT CAP RATE " && isEditingCapRate ? (
                <input
                  className="head-2 dark-H mt-1 text-center w-full border border-gray-300 rounded-md"
                  value={editedCapRate}
                  onChange={(e) => setEditedCapRate(e.target.value)}
                  onBlur={handleSaveCapRate}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSaveCapRate();
                    }
                  }}
                  autoFocus
                />
              ) : (
                <h3 className="head-2 dark-H mt-1">{value}</h3>
              )}
            </div>
          ))}

          <div
            className="p-4 rounded-2xl shadow-sm text-center cursor-pointer bg-gray-100"
            onClick={() => {
              setShowCommentBox(true);
              setComment(contractData?.comment || "");
            }}
          >
            {!showCommentBox ? (
              contractData?.comment ? (
                <p className="head-4 dark-H">
                  {contractData.comment}
                </p>
              ) : (
                <p className="body-S dark-M">Click here to add comment</p>
              )
            ) : (
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCommentSubmit();
                  }
                }}
                placeholder="Type your comment here..."
                className="mt-1 w-full p-2 border border-gray-400 rounded-xl"
                rows="3"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-[2fr_1fr_1fr] gap-6 items-stretch">

          <div className="p-5 rounded-2xl shadow-md bg-gray-100 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="uppercase head-2 green-H mb-3">Milestones</h3>

              <button onClick={() => navigate(`/edit-contact-close/${id}`)}>
                <img src={Edit} alt="icon" className="sidebar-icons mr-3" />
              </button>
            </div>


            <div className="flex-grow overflow-x-auto overflow-y-auto max-h-[400px]">
              <table className="w-full text-sm text-gray-800 border-collapse">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 w-[35%] font-semibold">
                      Milestone
                    </th>
                    <th className="text-left py-2 px-2 w-[20%] font-semibold">
                      Due Date
                    </th>
                    <th className="text-left py-2 px-2 w-[25%] font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {milestoneRows.map(([label, key], idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-200 hover:bg-gray-200 transition"
                    >
                      <td className="py-2 body-N px-2 whitespace-pre-line">
                        {label}
                      </td>

                      <td className="py-2 body-N px-2">
                        {contractData?.[key] || ""}
                      </td>

                      <td className="py-2 px-2 body-N">
                        {milestones[idx]?.status === "Completed" ? (
                          <span
                            className="body-N  cursor-pointer"
                            onClick={() =>
                              handleStatusChange(idx, "In Progress")
                            }
                          >
                            {milestones[idx]?.rawCompletedDate}
                          </span>
                        ) : milestones[idx]?.status === "In Progress" ? (
                          <span
                            className="text-yellow-700 text-lg cursor-pointer"
                            onClick={() =>
                              handleStatusChange(idx, "Completed")
                            }
                          >
                            ⏳
                          </span>
                        ) : (
                          <select
                            className="border border-gray-400 rounded-md text-sm px-1 py-1 bg-white"
                            value={milestones[idx]?.status || "Pending"}
                            onChange={(e) =>
                              handleStatusChange(idx, e.target.value)
                            }
                          >
                            <option>Pending</option>
                            <option>In Progress</option>
                            <option>Completed</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-5 rounded-2xl shadow-md bg-gray-100 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="uppercase head-2 text-green-800">Key Contacts</h3>

              <button
                className=""
                onClick={() => setIsModalOpen(true)}
              >
                <img src={Edit} alt="icon" className="sidebar-icons mr-3" />
              </button>
            </div>
            <div className="text-sm text-gray-800 flex-grow overflow-auto space-y-4">
              <div>
                <p className="font-semibold text-gray-900">
                  {contractData?.escrowAgent?.title || "Escrow Agent"}:
                </p>
                <div className="ml-4">
                  <p>
                    {contractData?.key_contact?.agent_company_name?.name}
                  </p>
                  <p>
                    {contractData?.key_contact?.agent?.name}
                  </p>
                  <p>
                    {contractData?.key_contact?.agent?.phone}
                  </p>
                  <p>
                    {contractData?.key_contact?.agent?.email}
                  </p>
                </div>
              </div>

              <hr className="border-gray-300" />

              <div>
                <p className="font-semibold text-gray-900">Seller:</p>

                <div className="ml-4 space-y-3">
                  {/* Seller Disposition */}
                  {contractData?.key_contact?.seller?.seller_disposition?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_disposition.map((seller, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${seller.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {seller.first_name} {seller.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Seller</p>
                    </div>
                  )}

                  {/* Seller Broker Inside */}
                  {contractData?.key_contact?.seller?.seller_inside?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_inside.map((broker, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${broker.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {broker.first_name} {broker.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Broker Inside</p>
                    </div>
                  )}

                  {/* Seller Broker Outside */}
                  {contractData?.key_contact?.seller?.seller_outside?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_outside.map((broker, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${broker.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {broker.first_name} {broker.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Broker Outside</p>
                    </div>
                  )}

                  {/* Seller BDS */}
                  {contractData?.key_contact?.seller?.seller_bds?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_bds.map((bd, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${bd.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {bd.first_name} {bd.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>BDS</p>
                    </div>
                  )}

                  {/* Seller Attorney */}
                  {contractData?.key_contact?.seller?.seller_attorney?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_attorney.map((att, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${att.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {att.first_name} {att.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Attorney</p>
                    </div>
                  )}

                  {/* Seller Business Partner */}
                  {contractData?.key_contact?.seller?.seller_business_partner?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_business_partner.map((bp, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${bp.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {bp.first_name} {bp.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Business Partner</p>
                    </div>
                  )}

                  {/* Seller Other */}
                  {contractData?.key_contact?.seller?.seller_other?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_other.map((other, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${other.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {other.first_name} {other.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Other</p>
                    </div>
                  )}
                </div>
              </div>

              <hr className="border-gray-300" />

              <div>
                <p className="font-semibold text-gray-900">Buyer:</p>

                <div className="ml-4 space-y-3">
                  {/* Buyer Acquisition */}
                  {contractData?.key_contact?.buyer?.buyer_acquistion?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_acquistion.map((broker, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${broker.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {broker.first_name} {broker.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Buyer</p>
                    </div>
                  )}

                  {/* Buyer Broker Inside */}
                  {contractData?.key_contact?.buyer?.buyer_inside?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_inside.map((broker, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${broker.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {broker.first_name} {broker.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Broker Inside</p>
                    </div>
                  )}

                  {/* Buyer Broker Outside */}
                  {contractData?.key_contact?.buyer?.buyer_outside?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_outside.map((broker, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${broker.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {broker.first_name} {broker.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Broker Outside</p>
                    </div>
                  )}

                  {/* Buyer BDs */}
                  {contractData?.key_contact?.buyer?.buyer_bds?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_bds.map((bd, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${bd.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {bd.first_name} {bd.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>BDS</p>
                    </div>
                  )}

                  {/* Buyer Attorney */}
                  {contractData?.key_contact?.buyer?.buyer_attorney?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_attorney.map((att, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${att.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {att.first_name} {att.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Attorney</p>
                    </div>
                  )}

                  {/* Buyer Business Partner */}
                  {contractData?.key_contact?.buyer?.buyer_business_partner?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_business_partner.map((bp, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${bp.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {bp.first_name} {bp.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Business Partner</p>
                    </div>
                  )}

                  {/* Buyer Other */}
                  {contractData?.key_contact?.buyer?.buyer_other?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_other.map((other, i) => (
                          <p key={i}>
                            <a
                              href={`/contact/${other.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {other.first_name} {other.last_name}
                            </a>
                          </p>
                        ))}
                      </strong>
                      <p>Other</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          <div className="p-5 rounded-2xl shadow-md bg-gray-100 flex flex-col overflow-x-auto max-h-[480px]">
            <div className="flex justify-between items-start mb-3">
              <h3 className="uppercase head-2 text-green-800 mb-3">
                Commission Breakdown
              </h3>
              <button
                className=""
                onClick={() => setModalOpen(true)}
              >
                <img src={Edit} alt="icon" className="sidebar-icons mr-3" />
              </button>
            </div>

            <div className="text-sm text-gray-800 flex-grow overflow-auto space-y-1">

              {/* Total Commission */}
              <div className="flex justify-between font-semibold ">
                <p>Total Commission:</p>
                <p>
                  $
                  {contractData?.total_commission_price
                    ? contractData.total_commission_price.toLocaleString()
                    : contractData?.executed?.gross_commission?.gci_to_company || 0}
                </p>
              </div>

              {/* Broker of Record Fee */}
              <div className="flex justify-between">
                <p className="font-semibold">Broker of Record Fee:</p>
                <p>
                  $
                  {contractData?.broker_record_fee
                    ? contractData.broker_record_fee.toLocaleString()
                    : "0.00"}
                </p>
              </div>

              {/* Referral Fee */}
              <div className="flex justify-between">
                <p className="font-semibold">Referral Fee:</p>
                <p>
                  $
                  {contractData?.referal_fee
                    ? contractData.referal_fee
                    : "0.00"}
                </p>
              </div>

              {/* Misc Fee */}
              <div className="flex justify-between">
                <p className="font-semibold">Misc. Fee:</p>
                <p>
                  $
                  {contractData?.misc_fee
                    ? contractData.misc_fee
                    : "0.00"}
                </p>
              </div>

              {/* Gross Commission Income */}
              <div className="flex justify-between font-semibold  mt-2">
                <p>Gross Commission Income:</p>
                <p>${contractData?.gross_commission_income_format ? contractData?.gross_commission_income_format
                  : contractData?.executed?.gross_commission?.gci_to_company || 0}</p>
              </div>

              <hr className="my-2 border-gray-300" />

              {/* Buyer Side Brokers */}
              <p className="font-semibold text-gray-900 mt-3">Pay to Brokers</p>

              {contractData?.broker_breakdowns?.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <div>
                    <p className="font-semibold">{item.broker_name}</p>
                    <p className="text-xs text-gray-500">{item.percentage}% Contribution</p>
                  </div>
                  <p>${item.total_value_format}</p>
                </div>
              ))}

              {/* Pay to BDS */}
              <p className="font-semibold text-gray-900 mt-3">Pay to BDS</p>

              {contractData?.bds_breakdowns?.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <div>
                    <p className="font-semibold">{item.bds_name}</p>
                    <p className="text-xs text-gray-500">{item.percentage}% Contribution</p>
                  </div>
                  <p>${item.total_value_format}</p>
                </div>
              ))}

              <hr className="my-2 border-gray-300" />

              {/* Company Gross Commission */}
              <div className="flex justify-between font-semibold ">
                <p>Company Gross Commission:</p>
                <p>${contractData?.company_gross_commission_format ?? calculateCompanyGross()}</p>
              </div>
            </div>
          </div>

        </div>

        <AddKeyContract
          showModal={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            fetchContractDetails();
          }}
          id={id}
        />

        <AddCommission
          showModal={modalOpen}
          onClose={() => {
            setModalOpen(false);
            fetchContractDetails();
          }}
          id={id}
        />

        <CompleteStatusModal
          showModal={completeModal}
          onClose={() => setCompleteModal(false)}
          onSave={saveCompletedDate}
        />
      </div>
    </BaseLayout>
  );
};

export default StageDetailPage;